import { useEffect, useState, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  X,
  RefreshCw,
  Moon,
  Sun,
  LogOut,
  ReceiptText,
  ScrollText,
} from "lucide-react";
import { toast } from "sonner";
import { forceSyncDevice } from "@/lib/forceSync";
import ConnectMyTelegramButton from "@/components/ConnectMyTelegram";
import { CaptainAttendanceWidget } from "@/components/captain/CaptainAttendanceWidget";
import { featureAllowed } from "@/lib/access";

export default function UnifiedMobileDrawer({
  open,
  onClose,
  groups = [],
  user,
  permissions,
  theme,
  setTheme,
  onExit,
  token,
  checkServer,
  isCaptainLayout = false,
}) {
  const location = useLocation();
  const [reloading, setReloading] = useState(false);
  const drawerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (open) onClose();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Body scroll lock & focus restoration
  useEffect(() => {
    if (!open) return;
    previousActiveElement.current = document.activeElement;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const timer = setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 50);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = originalOverflow;
      if (previousActiveElement.current && typeof previousActiveElement.current.focus === "function") {
        previousActiveElement.current.focus();
      }
    };
  }, [open]);

  // Escape key & focus trap
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const container = drawerRef.current;
        if (!container) return;
        const focusable = container.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const pendingAllowed = featureAllowed("orders.pending_bills", user, permissions);

  const reloadWithSync = async () => {
    if (reloading) return;
    setReloading(true);
    let result;
    try {
      result = await forceSyncDevice(token, checkServer);
    } catch (error) {
      result = { ok: false, message: `Sync failed on this device: ${error?.message || error}` };
    }
    if (result.ok) {
      window.location.reload();
      return;
    }
    setReloading(false);
    toast.error(result.message, {
      duration: 15000,
      action: { label: "Reload anyway", onClick: () => window.location.reload() },
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
      data-testid="mobile-menu"
    >
      {/* Backdrop */}
      <button
        type="button"
        className="fixed inset-0 bg-black/60 backdrop-blur-xs cursor-default"
        aria-label="Close menu"
        onClick={onClose}
      />

      {/* Drawer slide-over */}
      <div ref={drawerRef} className="relative w-72 max-w-[85vw] h-full bg-card border-r shadow-2xl flex flex-col z-10 animate-in slide-in-from-left duration-200">
        {/* Brand & User Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b shrink-0 bg-card">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold text-lede shadow-inner shrink-0">
              V
            </span>
            <div className="flex flex-col min-w-0 leading-tight">
              <strong className="text-sm font-bold tracking-tight text-foreground truncate">
                RestoYaar POS
              </strong>
              <span className="text-micro text-muted-foreground truncate">
                {user?.name || "Staff"} · <span className="capitalize">{user?.role_name || user?.role}</span>
              </span>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground transition-colors shrink-0"
            aria-label="Close menu"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-4" data-testid="primary-navigation">
          {groups.map((group) => (
            <div key={group.name} className="space-y-1">
              <div className="px-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {group.name}
              </div>
              {group.links.map(({ to, end, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={onClose}
                  data-testid={`mobile-nav-${label.toLowerCase().replaceAll(" ", "-")}`}
                  title={label}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`
                  }
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                  <span className="truncate">{label}</span>
                </NavLink>
              ))}
            </div>
          ))}

          {/* Captain specific extra tools */}
          {(isCaptainLayout || pendingAllowed) && (
            <div className="space-y-1">
              <div className="px-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Captain
              </div>
              {pendingAllowed && (
                <NavLink
                  to="/captain/pending-bills"
                  onClick={onClose}
                  data-testid="captain-drawer-pending-bills"
                  title="Pending bills"
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`
                  }
                >
                  <ReceiptText className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                  <span className="truncate">Pending bills</span>
                </NavLink>
              )}
              {isCaptainLayout && (
                <NavLink
                  to="/captain/logging"
                  onClick={onClose}
                  data-testid="captain-drawer-logging"
                  title="Logging"
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`
                  }
                >
                  <ScrollText className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                  <span className="truncate">Logging</span>
                </NavLink>
              )}
            </div>
          )}
        </nav>

        {/* Footer Actions */}
        <div className="p-3 border-t space-y-1.5 shrink-0 bg-card">
          {/* Attendance widget pill */}
          <div className="pb-1 flex justify-center">
            <CaptainAttendanceWidget token={token} />
          </div>

          {/* Sync & reload button */}
          <button
            type="button"
            onClick={reloadWithSync}
            disabled={reloading}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs sm:text-sm font-medium text-foreground hover:bg-muted transition-colors active:scale-98"
            data-testid="drawer-sync-reload"
          >
            <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 ${reloading ? "animate-spin" : ""}`} />
            <span>{reloading ? "Syncing…" : "Sync & reload"}</span>
          </button>

          {/* Telegram connect */}
          <ConnectMyTelegramButton
            token={token}
            testId="drawer-connect-telegram"
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs sm:text-sm font-medium text-foreground hover:bg-muted transition-colors"
          />

          {/* Theme toggle */}
          <button
            type="button"
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs sm:text-sm font-medium text-foreground hover:bg-muted transition-colors"
            data-testid="theme-toggle-button"
          >
            {theme === "light" ? (
              <Moon className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
            ) : (
              <Sun className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
            )}
            <span>{theme === "light" ? "Night mode" : "Day mode"}</span>
          </button>

          {/* Sign out */}
          <button
            type="button"
            onClick={onExit}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs sm:text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
            <span>Sign out</span>
          </button>
        </div>
      </div>
    </div>
  );
}
